/**
 * Unified Primary Mathematics Data Service
 * 
 * This service provides a unified interface for accessing Primary Mathematics data
 * using A2A capabilities. It replaces direct Supabase calls and provides:
 * - Grade data loading (P1-P6)
 * - Drill question generation (via A2A)
 * - Caching with 5-minute TTL
 * - Fallback to Supabase if A2A is unavailable
 */

import a2aMathsService from '../api/services/a2aMathsService';
import { primaryMathsService as supabaseService } from './supabasePrimaryMathsService';

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const dataCache = {};

/**
 * Clear all cached data
 */
export const clearCache = () => {
  Object.keys(dataCache).forEach(key => delete dataCache[key]);
  console.log('[PrimaryMathsDataService] Cache cleared');
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry || !cacheEntry.timestamp) return false;
  const now = Date.now();
  return (now - cacheEntry.timestamp) < CACHE_DURATION;
};

/**
 * Transform A2A response to match expected format
 */
const transformA2AResponse = (a2aData) => {
  // A2A response format: { success: true, data: { grade, topics: [...] } }
  if (a2aData && a2aData.grade) {
    return a2aData;
  }
  
  // If data is nested in result
  if (a2aData && a2aData.data) {
    return a2aData.data;
  }
  
  return a2aData;
};

/**
 * Unified Primary Mathematics Data Service
 */
const primaryMathsDataService = {
  /**
   * Get all data for a specific grade (P1, P2, P3, P4, P5, P6)
   * Uses A2A capability get_primary_grade_data
   * 
   * @param {string} grade - Grade identifier (e.g., 'P1', 'P2')
   * @param {boolean} forceRefresh - Force refresh from server
   * @returns {Promise<Object|null>} Grade data with topics and questions
   */
  async getGradeData(grade, forceRefresh = false) {
    const cacheKey = `grade_${grade}`;
    
    // Check cache first (unless forcing refresh)
    if (!forceRefresh && dataCache[cacheKey] && isCacheValid(dataCache[cacheKey])) {
      console.log(`[PrimaryMathsDataService] Returning cached data for ${grade}`);
      return dataCache[cacheKey].data;
    }

    try {
      // Try A2A first
      console.log(`[PrimaryMathsDataService] Fetching ${grade} data via A2A...`);
      const a2aResult = await a2aMathsService.getPrimaryGradeData(grade);
      const transformedData = transformA2AResponse(a2aResult);
      
      if (transformedData && transformedData.topics) {
        // Cache the result
        dataCache[cacheKey] = {
          data: transformedData,
          timestamp: Date.now()
        };
        
        console.log(`[PrimaryMathsDataService] ✓ Successfully loaded ${grade} data via A2A`);
        return transformedData;
      }
      
      throw new Error('A2A returned invalid data format');
    } catch (a2aError) {
      console.warn(`[PrimaryMathsDataService] A2A failed for ${grade}, falling back to Supabase:`, a2aError.message);
      
      // Fallback to Supabase
      try {
        const supabaseData = await supabaseService.getGradeData(grade, forceRefresh);
        if (supabaseData) {
          // Cache the result
          dataCache[cacheKey] = {
            data: supabaseData,
            timestamp: Date.now()
          };
          console.log(`[PrimaryMathsDataService] ✓ Loaded ${grade} data via Supabase fallback`);
        }
        return supabaseData;
      } catch (supabaseError) {
        console.error(`[PrimaryMathsDataService] Both A2A and Supabase failed for ${grade}:`, supabaseError);
        throw supabaseError;
      }
    }
  },

  /**
   * Generate drill questions using A2A capability
   * 
   * @param {Object} originalQuestion - Original question object
   * @param {string} grade - Grade (P1-P6)
   * @param {Object} options - Generation options
   * @param {string} options.topicId - Topic ID
   * @param {number} options.numberOfDrills - Number of drills (default: 5)
   * @param {Function} options.onProgress - Progress callback
   * @returns {Promise<Object>} Generated drill questions
   */
  async generateDrillQuestions(originalQuestion, grade, options = {}) {
    try {
      console.log('[PrimaryMathsDataService] Generating drill questions via A2A...');
      const result = await a2aMathsService.generatePrimaryDrillQuestions(
        originalQuestion,
        grade,
        options
      );
      
      // Transform to match expected format
      if (result.success && result.drillQuestions) {
        return result;
      }
      
      // If result is in A2A format, transform it
      if (result.drillQuestions || result.drill_questions) {
        return {
          success: true,
          drillQuestions: result.drillQuestions || result.drill_questions || [],
          originalQuestion: result.originalQuestion || originalQuestion,
          metadata: result.metadata || {}
        };
      }
      
      return result;
    } catch (error) {
      console.error('[PrimaryMathsDataService] Drill generation failed:', error);
      throw error;
    }
  },

  /**
   * Get all grades (P1-P6)
   * Uses A2A capability get_primary_grades
   * 
   * @returns {Promise<Object>} Grades list
   */
  async getGrades() {
    try {
      const result = await a2aMathsService.getPrimaryGrades();
      return result;
    } catch (error) {
      console.error('[PrimaryMathsDataService] Failed to get grades:', error);
      throw error;
    }
  },

  /**
   * Clear cache for a specific grade
   * 
   * @param {string} grade - Grade identifier
   */
  clearGradeCache(grade) {
    const cacheKey = `grade_${grade}`;
    delete dataCache[cacheKey];
    console.log(`[PrimaryMathsDataService] Cleared cache for ${grade}`);
  },

  /**
   * Clear all cached data
   */
  clearCache() {
    clearCache();
  }
};

export default primaryMathsDataService;

